// ---------------------------------------------------------------------------
// PWA_ControlBillet — Agent Client
// HTTP client for communicating with the backend via same-origin reverse proxy.
// Uses JWT HttpOnly cookies for authentication (credentials: 'include').
// ---------------------------------------------------------------------------

import { useAuthStore } from '@/store/auth-store';
import { API_TIMEOUT, MAX_RETRIES, RETRY_DELAYS } from '@/lib/constants';
import type { ValidationRequest, ValidationResponse, HistoryEntry } from '@/lib/types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SyncPayload {
  scans: Array<{
    ticket_id: string;
    event_id: number;
    validated_by: string;
    device_id: string;
    timestamp: string;
    checksum: string;
  }>;
}

interface SyncResponse {
  success: boolean;
  synced: number;
  errors: number;
}

interface StatusResponse {
  status: string;
  version?: string;
  uptime?: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getCsrfToken(): string {
  const match = document.cookie.match(/csrf_access_token=([^;]+)/);
  return match ? match[1] : '';
}

// ---------------------------------------------------------------------------
// Agent Client
// ---------------------------------------------------------------------------

class AgentClient {
  // ---- connection stats ---------------------------------------------------

  private stats = {
    totalRequests: 0,
    failedRequests: 0,
    responseTimes: [] as number[],
  };

  // ---- private helpers ----------------------------------------------------

  private getHeaders(mutating = false): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (mutating) {
      headers['X-CSRF-TOKEN'] = getCsrfToken();
    }

    return headers;
  }

  // ---- generic request with retry ----------------------------------------

  async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const url = `/api${endpoint}`;
    const isMutating = options?.method && options.method !== 'GET';
    const headers = {
      ...this.getHeaders(!!isMutating),
      ...(options?.headers as Record<string, string>),
    };

    this.stats.totalRequests++;
    const requestStart = Date.now();

    const delays = RETRY_DELAYS.slice(0, MAX_RETRIES);
    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), API_TIMEOUT);

      try {
        const response = await fetch(url, {
          ...options,
          headers,
          credentials: 'include',
          signal: controller.signal,
        });

        clearTimeout(timer);

        // Handle 401 — session expired, force logout.
        if (response.status === 401) {
          useAuthStore.getState().logout();
          throw new Error('Session expiree');
        }

        if (!response.ok) {
          const body = await response.text().catch(() => '');
          throw new Error(`HTTP ${response.status}: ${body}`);
        }

        const result = (await response.json()) as T;

        // Track successful response time
        const elapsed = Date.now() - requestStart;
        this.stats.responseTimes.push(elapsed);
        if (this.stats.responseTimes.length > 100) {
          this.stats.responseTimes.shift();
        }

        return result;
      } catch (err) {
        clearTimeout(timer);
        lastError = err instanceof Error ? err : new Error(String(err));

        // Do not retry on auth failure.
        if (lastError.message === 'Session expiree') {
          throw lastError;
        }

        if (attempt < MAX_RETRIES) {
          await this.delay(delays[attempt]);
        }
      }
    }

    // Track failed request
    this.stats.failedRequests++;
    const elapsed = Date.now() - requestStart;
    this.stats.responseTimes.push(elapsed);
    if (this.stats.responseTimes.length > 100) {
      this.stats.responseTimes.shift();
    }

    throw lastError ?? new Error('Request failed after retries');
  }

  // ---- public API ---------------------------------------------------------

  /**
   * Test connection to the backend.
   */
  async testConnection(): Promise<boolean> {
    try {
      await this.request<StatusResponse>('/health');
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Validate a ticket via the backend.
   */
  async validateTicket(data: ValidationRequest): Promise<ValidationResponse> {
    // Backend expects { ticket_code, event_id, device_id? } — map from ValidationRequest
    const payload = {
      ticket_code: data.ticket_id,
      event_id: data.event_id,
      device_id: data.device_id,
    };
    return this.request<ValidationResponse>('/tickets/validate', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  /**
   * Get validation history from the backend.
   */
  async getHistory(limit = 50, eventId?: number): Promise<HistoryEntry[]> {
    try {
      const params = new URLSearchParams({ limit: String(limit) });
      if (eventId != null) {
        params.set('event_id', String(eventId));
      }
      return await this.request<HistoryEntry[]>(`/tickets/history?${params.toString()}`);
    } catch {
      return [];
    }
  }

  /**
   * Sync offline scans to the backend.
   */
  async syncScans(scans: SyncPayload['scans']): Promise<SyncResponse> {
    return this.request<SyncResponse>('/tickets/scans', {
      method: 'POST',
      body: JSON.stringify({ scans }),
    });
  }

  /**
   * Heartbeat ping with diagnostic headers.
   */
  async heartbeat(): Promise<boolean> {
    try {
      const stats = this.getStats();
      await this.request<StatusResponse>('/health', {
        headers: {
          'X-Conn-Error-Rate': stats.errorRate,
          'X-Conn-Avg-Response': stats.avgResponse,
          'X-Conn-Total-Requests': String(stats.totalRequests),
        },
      });
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get connection statistics.
   */
  getStats(): { errorRate: string; avgResponse: string; totalRequests: number } {
    const { totalRequests, failedRequests, responseTimes } = this.stats;
    const errorRate =
      totalRequests > 0 ? ((failedRequests / totalRequests) * 100).toFixed(1) : '0.0';
    const avgResponse =
      responseTimes.length > 0
        ? (responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length).toFixed(2)
        : '0.00';
    return { errorRate, avgResponse, totalRequests };
  }

  // ---- utils --------------------------------------------------------------

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

// ---------------------------------------------------------------------------
// Singleton export
// ---------------------------------------------------------------------------

export const agentClient = new AgentClient();
